import { useEffect, useState } from "react";
import { Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, C, errorMessage, fmtChips } from "../../lib/api";
import { Card, Stat, fmtNum, fmtWhen, inputCls } from "./ui";
import { fmtAud } from "../store/StoreFlow";

const RANGES = [{ key: "today", label: "Today" }, { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 days" }, { key: "30d", label: "Last 30 days" }, { key: "all", label: "All time" }];
const DEFAULTS = { range: "today", q: "", package_id: "", kind: "" };

// Sales: every order ever paid for, with refunds. Its own tab so it can sit behind a PIN later.
export const SalesPanel = () => {
  const [purch, setPurch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refunding, setRefunding] = useState(null); // txn_id awaiting confirmation
  const [clawback, setClawback] = useState("");
  const [filters, setFilters] = useState(DEFAULTS);
  const load = (f = filters) => api.get(C.ApiRoutes.ADMIN_STORE_PURCHASES, {
    params: { ...f, tz_offset: -new Date().getTimezoneOffset(), limit: 200 },
  }).then((r) => setPurch(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { const t = setTimeout(() => load(filters), 250); return () => clearTimeout(t); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  const setFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const refund = async (t) => {
    setBusy(true);
    try {
      const chips = clawback.trim() === "" ? null : Number(clawback);
      const { data: r } = await api.post(C.ApiRoutes.ADMIN_STORE_REFUND.replace("{txn_id}", t.txn_id), { chips });
      toast.success(`Refunded ${fmtAud(r.amount_cents)} to ${t.player}` +
        (r.chips_reclaimed ? ` · ${fmtChips(r.chips_reclaimed)} taken back` : " · chips left alone") +
        (r.chips_shortfall ? ` · ${fmtChips(r.chips_shortfall)} they no longer had` : ""));
      setRefunding(null); setClawback(""); load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  if (!purch) return null;
  const dirty = filters.range !== DEFAULTS.range || filters.q || filters.package_id || filters.kind;
  return (
    <Card title="Sales" testId="sales-panel">
      <div className="flex flex-wrap items-end gap-2 mb-4" data-testid="order-filters">
        <select data-testid="order-range" value={filters.range} onChange={(e) => setFilter({ range: e.target.value })} className={`${inputCls} w-[150px]`}>
          {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <input data-testid="order-search" value={filters.q} onChange={(e) => setFilter({ q: e.target.value })}
          placeholder="Player, email, order ID or Stripe ref" className={`${inputCls} w-[260px]`} />
        <select data-testid="order-product" value={filters.package_id} onChange={(e) => setFilter({ package_id: e.target.value })} className={`${inputCls} w-[150px]`}>
          <option value="">All products</option>
          {(purch.products || []).map((p) => <option key={p.package_id} value={p.package_id}>{p.name}</option>)}
        </select>
        <select data-testid="order-kind" value={filters.kind} onChange={(e) => setFilter({ kind: e.target.value })} className={`${inputCls} w-[130px]`}>
          <option value="">All types</option>
          {(purch.kinds || []).map((k) => <option key={k} value={k}>{k === "timebank" ? "Time banks" : k === "chips" ? "Chips" : k}</option>)}
        </select>
        {dirty && (
          <button data-testid="order-filters-clear" onClick={() => setFilters(DEFAULTS)}
            className="rounded-full px-3 py-1.5 text-[10px] text-slate-400 hover:text-white hover:bg-white/5">Reset</button>
        )}
      </div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Stat label="Sales" value={fmtNum(purch.count)} testId="store-stat-count" />
        <Stat label="Revenue" value={fmtAud(purch.total_cents)} sub={purch.test_mode ? "test mode — nothing charged" : "AUD, refunds excluded"} testId="store-stat-revenue" />
        <Stat label="Refunded" value={fmtAud(purch.refunded_cents)} sub={`${fmtNum(purch.refunded_count)} order${purch.refunded_count === 1 ? "" : "s"}`} testId="store-stat-refunded" />
        <Stat label="Chips sold" value={fmtChips(purch.total_chips)} testId="store-stat-chips" />
      </div>
      <table className="w-full text-xs">
        <thead><tr className="text-[9px] uppercase tracking-[0.2em] text-slate-400">
          <th className="text-left font-normal pb-1">When</th><th className="text-left font-normal pb-1">Player</th>
          <th className="text-left font-normal pb-1">Product</th><th className="text-right font-normal pb-1">Granted</th>
          <th className="text-right font-normal pb-1">Paid</th><th className="text-left font-normal pb-1 pl-3">Order / Stripe</th>
          <th className="text-left font-normal pb-1">Status</th><th className="text-right font-normal pb-1">Refund</th>
        </tr></thead>
        <tbody>
          {purch.purchases.map((t) => (
            <tr key={t.txn_id} className="border-t border-white/5 align-top" data-testid="store-purchase-row">
              <td className="py-2 text-slate-300 whitespace-nowrap">{fmtWhen(t.paid_at)}</td>
              <td className="py-2"><div className="text-amber-50">{t.player}</div><div className="text-[10px] text-slate-500">{t.email}</div></td>
              <td className="py-2 text-amber-50">{t.package.name}</td>
              <td className="py-2 text-right whitespace-nowrap">
                <div className="font-mono text-amber-100">{fmtChips(t.chips)}</div>
                {t.vip > 0 && <div className="text-[10px] text-violet-300">{t.vip} VIP</div>}
                {t.cards > 0 && <div className="text-[10px] text-cyan-300">{t.cards} card{t.cards > 1 ? "s" : ""}</div>}
              </td>
              <td className="py-2 text-right font-mono text-emerald-300 whitespace-nowrap">{fmtAud(t.amount_cents)}</td>
              <td className="py-2 pl-3 font-mono text-[10px] text-slate-500 break-all max-w-[170px]">
                <div className="text-slate-400">{t.txn_id}</div>
                <div>{t.provider_ref || "—"}</div>
                {t.refund_id && <div className="text-amber-500/70">{t.refund_id}</div>}
              </td>
              <td className="py-2">
                <span data-testid={`order-status-${t.txn_id}`} className={`rounded-full px-2 py-0.5 text-[10px] border ${t.status === "refunded" ? "border-amber-400/40 text-amber-200 bg-amber-400/10" : "border-emerald-400/40 text-emerald-200"}`}>{t.status}</span>
                {t.status === "refunded" && <div className="text-[10px] text-slate-500 mt-1">{t.chips_reclaimed ? `${fmtChips(t.chips_reclaimed)} taken back` : "chips left alone"}{t.chips_shortfall ? ` · ${fmtChips(t.chips_shortfall)} short` : ""}</div>}
              </td>
              <td className="py-2 text-right">
                {t.refundable && (refunding === t.txn_id ? (
                  <div className="flex flex-col items-end gap-1.5" data-testid={`refund-confirm-${t.txn_id}`}>
                    <input data-testid={`refund-chips-${t.txn_id}`} type="number" min="0" value={clawback} onChange={(e) => setClawback(e.target.value)}
                      placeholder="chips to take back (optional)" className={`${inputCls} w-[190px] text-right`} />
                    <div className="flex gap-1.5">
                      <button data-testid={`refund-cancel-${t.txn_id}`} onClick={() => setRefunding(null)} className="rounded-full px-3 py-1 text-[10px] bg-white/5 hover:bg-white/10">Cancel</button>
                      <button data-testid={`refund-go-${t.txn_id}`} disabled={busy} onClick={() => refund(t)} className="rounded-full px-3 py-1 text-[10px] bg-red-600/80 hover:bg-red-500 text-white disabled:opacity-50">{busy ? "Refunding…" : `Refund ${fmtAud(t.amount_cents)}`}</button>
                    </div>
                  </div>
                ) : (
                  <button data-testid={`refund-${t.txn_id}`} onClick={() => { setRefunding(t.txn_id); setClawback(""); }}
                    className="rounded-full px-3 py-1 text-[10px] border border-white/15 text-slate-300 hover:border-red-400/50 hover:text-red-200 flex items-center gap-1 ml-auto"><Undo2 className="w-3 h-3" /> Refund</button>
                ))}
              </td>
            </tr>
          ))}
          {purch.purchases.length === 0 && <tr><td colSpan={8} className="py-4 text-center text-slate-500">No sales match these filters.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
};
